import axios, { AxiosRequestConfig } from 'axios';
import {
    ThesaurusShare,
    CreateThesaurusShareRequest,
    UpdateThesaurusShareRequest,
    EntreeShare,
    CreateEntreeShareRequest,
    UpdateEntreeShareRequest,
    EntreeShareHistory,
    PaginatedHistoryResponse,
    ThesaurusSharePermission
} from '../../types/sharedThesaurus';

const thesaurusSharesBaseUrl = '/api/thesaurus-shares';
const entreeSharesBaseUrl = '/api/entree-shares';
const historyBaseUrl = '/api/entree-share-history';

// Fonction pour créer la configuration avec authentification
const createAuthConfig = (token?: string): AxiosRequestConfig => {
    if (!token) return {};
    return {
        headers: {
            'Authorization': `Bearer ${token}`,
            'accesstoken': token,
            'Content-Type': 'application/json'
        }
    };
};

// Variable pour stocker le token actuel
let currentToken: string | null = null;

// Fonction pour définir le token
export const setAuthToken = (token: string | null) => {
    currentToken = token;
};

/**
 * Service API pour les thésaurus partagés
 */
export const sharedThesaurusService = {
    // =====================
    // Thésaurus partagés
    // =====================

    /**
     * Récupère tous les thésaurus partagés (créés par moi + partagés avec moi)
     */
    getAll: async (filter?: 'owned' | 'shared'): Promise<{
        owned: ThesaurusShare[];
        sharedWithMe: ThesaurusShare[];
        total: number;
    }> => {
        const params: any = {};
        if (filter) params.filter = filter;
        
        console.log('🔍 [sharedThesaurusService.getAll] Appel API /api/thesaurus-shares...');
        try {
            const response = await axios.get(thesaurusSharesBaseUrl, {
                params,
                ...createAuthConfig(currentToken || undefined)
            });
            console.log('✅ [sharedThesaurusService.getAll] Réponse:', response.data);
            return response.data.data;
        } catch (error: any) {
            console.error('❌ [sharedThesaurusService.getAll] Erreur:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Récupère un thésaurus partagé par ID avec ses entrées
     */
    getById: async (id: string): Promise<ThesaurusShare & { entreeShares: EntreeShare[] }> => {
        const response = await axios.get(`${thesaurusSharesBaseUrl}/${id}`, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Crée un nouveau thésaurus partagé
     */
    create: async (data: CreateThesaurusShareRequest): Promise<ThesaurusShare> => {
        const response = await axios.post(thesaurusSharesBaseUrl, data, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Met à jour un thésaurus partagé
     */
    update: async (id: string, data: UpdateThesaurusShareRequest): Promise<ThesaurusShare> => {
        const response = await axios.put(`${thesaurusSharesBaseUrl}/${id}`, data, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Supprime un thésaurus partagé
     */
    delete: async (id: string): Promise<void> => {
        await axios.delete(`${thesaurusSharesBaseUrl}/${id}`, 
            createAuthConfig(currentToken || undefined)
        );
    },

    /**
     * Ajoute un participant au thésaurus
     */
    addParticipant: async (
        thesaurusShareId: string,
        participant: {
            userId: string;
            displayName: string;
            email?: string;
            avatar?: string;
            permission?: ThesaurusSharePermission;
        }
    ): Promise<ThesaurusShare> => {
        const response = await axios.post(
            `${thesaurusSharesBaseUrl}/${thesaurusShareId}`,
            {
                action: 'addParticipant',
                participantUserId: participant.userId,
                participantDisplayName: participant.displayName,
                participantEmail: participant.email,
                participantAvatar: participant.avatar,
                permission: participant.permission || 'read'
            },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Retire un participant du thésaurus
     */
    removeParticipant: async (
        thesaurusShareId: string,
        participantUserId: string
    ): Promise<ThesaurusShare> => {
        const response = await axios.post(
            `${thesaurusSharesBaseUrl}/${thesaurusShareId}`,
            {
                action: 'removeParticipant',
                participantUserId
            },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Met à jour la permission d'un participant
     */
    updateParticipantPermission: async (
        thesaurusShareId: string,
        participantUserId: string,
        permission: ThesaurusSharePermission
    ): Promise<ThesaurusShare> => {
        const response = await axios.post(
            `${thesaurusSharesBaseUrl}/${thesaurusShareId}`,
            {
                action: 'updateParticipant',
                participantUserId,
                permission
            },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Copie une entrée propriétaire vers le thésaurus partagé
     */
    copyEntryToShared: async (
        thesaurusShareId: string,
        entreeId: string
    ): Promise<EntreeShare> => {
        const response = await axios.post(
            `${thesaurusSharesBaseUrl}/${thesaurusShareId}`,
            {
                action: 'copyEntry',
                entreeId
            },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Met à jour les paramètres du thésaurus (rétention historique)
     */
    updateSettings: async (
        thesaurusShareId: string,
        settings: { historyRetentionMonths?: number }
    ): Promise<ThesaurusShare> => {
        const response = await axios.post(
            `${thesaurusSharesBaseUrl}/${thesaurusShareId}`,
            {
                action: 'updateSettings',
                ...settings
            },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    // =====================
    // Entrées partagées
    // =====================

    /**
     * Récupère les entrées d'un thésaurus partagé
     */
    getEntreeShares: async (
        thesaurusShareId: string,
        search?: string
    ): Promise<EntreeShare[]> => {
        const params: any = { thesaurusShareId };
        if (search) params.q = search;
        
        const response = await axios.get(entreeSharesBaseUrl, {
            params,
            ...createAuthConfig(currentToken || undefined)
        });
        return response.data.data;
    },

    /**
     * Récupère les entrées de TOUS les thésaurus partagés accessibles.
     *
     * Sert au menu `@` du chat RAG, qui doit proposer les entrées partagées
     * sans connaître les thésaurus un par un. `getEntreeShares` impose un
     * `thesaurusShareId` et obligerait à une requête par thésaurus.
     */
    getAllEntreeShares: async (search?: string): Promise<EntreeShare[]> => {
        const params: any = {};
        if (search) params.q = search;

        const response = await axios.get(entreeSharesBaseUrl, {
            params,
            ...createAuthConfig(currentToken || undefined)
        });
        return response.data.data;
    },

    /**
     * Récupère une entrée partagée par ID
     */
    getEntreeShareById: async (id: string): Promise<EntreeShare> => {
        const response = await axios.get(`${entreeSharesBaseUrl}/${id}`, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Crée une nouvelle entrée partagée
     */
    createEntreeShare: async (data: CreateEntreeShareRequest): Promise<EntreeShare> => {
        const response = await axios.post(entreeSharesBaseUrl, data, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Met à jour une entrée partagée
     */
    updateEntreeShare: async (id: string, data: UpdateEntreeShareRequest): Promise<EntreeShare> => {
        const response = await axios.put(`${entreeSharesBaseUrl}/${id}`, data, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Supprime une entrée partagée
     */
    deleteEntreeShare: async (id: string): Promise<void> => {
        await axios.delete(`${entreeSharesBaseUrl}/${id}`, 
            createAuthConfig(currentToken || undefined)
        );
    },

    /**
     * Copie une entrée partagée vers les entrées personnelles
     * Retourne 409 si une entrée avec la même clé existe déjà
     */
    copyToMyEntries: async (entreeShareId: string): Promise<any> => {
        const response = await axios.post(
            `${entreeSharesBaseUrl}/${entreeShareId}`,
            { action: 'copyToMyEntries' },
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    // =====================
    // Historique
    // =====================

    /**
     * Récupère l'historique d'un thésaurus partagé
     */
    getHistoryByThesaurus: async (
        thesaurusShareId: string,
        options?: {
            page?: number;
            limit?: number;
            action?: string;
            startDate?: string;
            endDate?: string;
        }
    ): Promise<PaginatedHistoryResponse> => {
        const response = await axios.get(historyBaseUrl, {
            params: {
                thesaurusShareId,
                ...options
            },
            ...createAuthConfig(currentToken || undefined)
        });
        return response.data;
    },

    /**
     * Récupère l'historique d'une entrée partagée
     */
    getHistoryByEntree: async (
        entreeShareId: string,
        options?: {
            page?: number;
            limit?: number;
        }
    ): Promise<PaginatedHistoryResponse> => {
        const response = await axios.get(historyBaseUrl, {
            params: {
                entreeShareId,
                ...options
            },
            ...createAuthConfig(currentToken || undefined)
        });
        return response.data;
    },

    /**
     * Purge l'ancien historique
     */
    purgeHistory: async (
        thesaurusShareId: string,
        retentionMonths?: number
    ): Promise<{ deletedCount: number }> => {
        const params: any = {};
        if (retentionMonths) params.retentionMonths = retentionMonths;
        
        const response = await axios.delete(`${historyBaseUrl}/purge/${thesaurusShareId}`, {
            params,
            ...createAuthConfig(currentToken || undefined)
        });
        return response.data.data;
    }
};
